export type ClassroomNoteFormat = Readonly<{
  backgroundColor?: string;
  color?: string;
  fontSize?: string;
  fontWeight?: string;
}>;

export function noteFormatRange(editor: HTMLElement, saved: Range | null): Range {
  const range = saved?.cloneRange() ?? document.createRange();
  if (saved === null || !editor.contains(range.commonAncestorContainer) || range.collapsed) {
    range.selectNodeContents(editor);
  }
  return range;
}

export function noteRangeIsBold(editor: HTMLElement, range: Range): boolean {
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let found = false;
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (!node.textContent || !range.intersectsNode(node)) continue;
    if (node === range.startContainer && range.startOffset === node.textContent.length) continue;
    if (node === range.endContainer && range.endOffset === 0) continue;
    const parent = node.parentElement;
    if (parent === null || Number.parseInt(getComputedStyle(parent).fontWeight, 10) < 600)
      return false;
    found = true;
  }
  return found;
}

// Split inline ancestors at each selection boundary. This lets a subset of a
// highlighted/bold span return to normal without changing the unselected text.
function liftMarker(marker: HTMLElement, editor: HTMLElement): void {
  let parent = marker.parentElement;
  while (
    parent !== null &&
    parent !== editor &&
    ['inline', 'contents'].includes(getComputedStyle(parent).display)
  ) {
    const after = parent.cloneNode(false) as HTMLElement;
    while (marker.nextSibling !== null) after.append(marker.nextSibling);
    parent.after(marker, after);
    if (!parent.hasChildNodes()) parent.remove();
    if (!after.hasChildNodes()) after.remove();
    parent = marker.parentElement;
  }
}

export function formatNoteRange(
  editor: HTMLElement,
  range: Range,
  format: ClassroomNoteFormat,
): Range {
  if (range.collapsed) return range;
  const end = document.createElement('span');
  const start = document.createElement('span');
  const endRange = range.cloneRange();
  endRange.collapse(false);
  endRange.insertNode(end);
  const startRange = range.cloneRange();
  startRange.collapse(true);
  startRange.insertNode(start);
  liftMarker(end, editor);
  liftMarker(start, editor);
  const selected = document.createRange();
  selected.setStartAfter(start);
  selected.setEndBefore(end);
  const contents = selected.extractContents();
  const properties = Object.keys(format) as (keyof ClassroomNoteFormat)[];
  for (const element of contents.querySelectorAll<HTMLElement>('*')) {
    for (const property of properties) element.style[property] = '';
    if (format.fontWeight !== undefined && ['B', 'STRONG'].includes(element.tagName)) {
      element.style.fontWeight = 'inherit';
    }
    if (element.tagName === 'FONT') {
      if (format.color !== undefined) element.removeAttribute('color');
      if (format.fontSize !== undefined) element.removeAttribute('size');
    }
  }
  const formatted = document.createElement('span');
  Object.assign(formatted.style, format);
  formatted.append(contents);
  selected.insertNode(formatted);
  start.remove();
  end.remove();
  const result = document.createRange();
  result.selectNodeContents(formatted);
  return result;
}
